/**
 * HABIBI ARTS & CRAFTS — COMPREHENSIVE DIGITAL ASSET VAULT & ANTI-EXTENSION ENGINE
 * Copyright (c) Habibi Arts & Crafts. Curated by Qaiser Habibi.
 * Total DOM URL Scrubbing • Real-Time GPU Canvas Engine • Universal Asset Lockdown
 */
(function () {
  'use strict';

  const BLANK_DUMMY_DATA_URL = 'data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/>';

  // =========================================================================
  // 1. CANVAS SECURITY INTERCEPTORS (Blocks extensions querying <canvas>)
  // =========================================================================
  const originalToDataURL = HTMLCanvasElement.prototype.toDataURL;
  const originalToBlob = HTMLCanvasElement.prototype.toBlob;
  const originalGetImageData = CanvasRenderingContext2D.prototype.getImageData;

  HTMLCanvasElement.prototype.toDataURL = function () {
    if (this.dataset && this.dataset.habibiProtected === 'true') {
      return BLANK_DUMMY_DATA_URL;
    }
    return originalToDataURL.apply(this, arguments);
  };

  HTMLCanvasElement.prototype.toBlob = function (callback) {
    if (this.dataset && this.dataset.habibiProtected === 'true') {
      const blob = new Blob([''], { type: 'image/png' });
      if (typeof callback === 'function') callback(blob);
      return;
    }
    return originalToBlob.apply(this, arguments);
  };

  CanvasRenderingContext2D.prototype.getImageData = function () {
    if (this.canvas && this.canvas.dataset && this.canvas.dataset.habibiProtected === 'true') {
      return this.createImageData(1, 1);
    }
    return originalGetImageData.apply(this, arguments);
  };

  // =========================================================================
  // 2. RESOURCE TIMING LOG WIPER (Blocks network scraping extensions)
  // =========================================================================
  if (window.performance && window.performance.clearResourceTimings) {
    setInterval(() => {
      try { window.performance.clearResourceTimings(); } catch (e) {}
    }, 1000);
  }

  // =========================================================================
  // 3. UI PROTECTION TOAST & PRIVACY GUARD
  // =========================================================================
  function initSecurityElements() {
    if (!document.getElementById('habibi-protection-toast')) {
      const toast = document.createElement('div');
      toast.id = 'habibi-protection-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      toast.innerHTML = `
        <span class="habibi-toast-icon" aria-hidden="true">✦</span>
        <span class="habibi-toast-text">
          <strong>Habibi Arts & Crafts</strong> • Proprietary Fine Art Design Protected
        </span>
      `;
      (document.body || document.documentElement).appendChild(toast);
    }

    if (!document.getElementById('habibi-screenshot-guard')) {
      const guard = document.createElement('div');
      guard.id = 'habibi-screenshot-guard';
      guard.innerHTML = `
        <div class="habibi-guard-backdrop" id="habibi-guard-backdrop"></div>
        <div class="habibi-guard-card" role="dialog" aria-modal="true" aria-labelledby="habibi-guard-heading">
          <div class="habibi-guard-corner habibi-guard-corner--tl" aria-hidden="true"></div>
          <div class="habibi-guard-corner habibi-guard-corner--tr" aria-hidden="true"></div>
          <div class="habibi-guard-corner habibi-guard-corner--bl" aria-hidden="true"></div>
          <div class="habibi-guard-corner habibi-guard-corner--br" aria-hidden="true"></div>
          <div class="habibi-guard-glow" aria-hidden="true"></div>

          <div class="habibi-guard-seal" aria-hidden="true">
            <div class="habibi-guard-seal-ring">
              <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                <path d="M12 8v4"></path>
                <circle cx="12" cy="16" r="0.8" fill="currentColor"></circle>
              </svg>
            </div>
          </div>

          <span class="habibi-guard-eyebrow">✦ HABIBI ARTS &amp; CRAFTS • ARCHIVAL GALLERY ✦</span>

          <h2 class="habibi-guard-title" id="habibi-guard-heading">
            Proprietary Artwork <em>Protected</em>
          </h2>

          <div class="habibi-guard-flourish" aria-hidden="true">
            <span class="habibi-flourish-line"></span>
            <span class="habibi-flourish-star">✦</span>
            <span class="habibi-flourish-line"></span>
          </div>

          <p class="habibi-guard-msg">
            Original museum-grade fine art digital prints curated by master artist <strong>Qaiser Habibi</strong>.<br>
            Screen capture, recording, and digital extraction are restricted to preserve copyright &amp; archival authenticity.
          </p>

          <div class="habibi-guard-actions">
            <button type="button" class="habibi-guard-btn" id="habibi-guard-dismiss">
              <span>Resume Gallery Viewing</span>
              <span class="habibi-guard-arrow" aria-hidden="true">→</span>
            </button>
          </div>

          <div class="habibi-guard-footer">
            <span>Private acquisition, bespoke scaling &amp; curatorial inquiries:</span>
            <a href="https://wa.me/923274485859?text=Hello%20Habibi%20Arts%20%26%20Crafts%2C%20I%20would%20like%20to%20inquire%20about%20an%20artwork." target="_blank" rel="noopener noreferrer">
              WhatsApp Curatorial Desk (+92 327 4485859)
            </a>
          </div>
        </div>
      `;
      (document.body || document.documentElement).appendChild(guard);

      const dismissBtn = document.getElementById('habibi-guard-dismiss');
      if (dismissBtn) {
        dismissBtn.addEventListener('click', function (e) {
          e.preventDefault();
          deactivateGuard();
        });
      }

      const backdrop = document.getElementById('habibi-guard-backdrop');
      if (backdrop) {
        backdrop.addEventListener('click', function () {
          deactivateGuard();
        });
      }
    }
  }

  let toastTimeout = null;
  function showToast(customMessage) {
    initSecurityElements();
    const toast = document.getElementById('habibi-protection-toast');
    if (!toast) return;

    if (customMessage) {
      const textEl = toast.querySelector('.habibi-toast-text');
      if (textEl) {
        textEl.innerHTML = `<strong>Habibi Arts & Crafts</strong> • ${customMessage}`;
      }
    }

    toast.classList.add('show');
    if (toastTimeout) clearTimeout(toastTimeout);
    toastTimeout = setTimeout(() => {
      toast.classList.remove('show');
    }, 3200);
  }

  const COPYRIGHT_NOTICE = '© HABIBI ARTS & CRAFTS — ARCHIVAL DIGITAL ASSET VAULT\nAll fine art prints, photography, and digital artworks curated by Qaiser Habibi are proprietary and protected under International Copyright & Intellectual Property Laws.\nUnauthorized screenshotting, digital reproduction, extraction, or commercial replication is strictly prohibited.\nOfficial Catalog & Inquiries: https://habibiartsncrafts.com';

  function blankAllCanvases() {
    const canvases = document.querySelectorAll('canvas.habibi-secure-canvas');
    canvases.forEach(canvas => {
      try {
        const ctx = canvas.getContext('2d');
        if (!ctx || !canvas.width || !canvas.height) return;
        ctx.save();
        ctx.fillStyle = '#0e0b09';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#C5A059';
        const fontSize = Math.max(14, Math.round(canvas.width / 24));
        ctx.font = `600 ${fontSize}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('HABIBI ARTS & CRAFTS', canvas.width / 2, canvas.height / 2 - 10);
        ctx.fillStyle = 'rgba(250, 244, 235, 0.55)';
        ctx.font = `400 ${Math.max(10, Math.round(canvas.width / 38))}px sans-serif`;
        ctx.fillText('Archival Artwork Protected', canvas.width / 2, canvas.height / 2 + 14);
        ctx.restore();
      } catch (e) {}
    });
  }

  function restoreAllCanvases() {
    const canvases = document.querySelectorAll('canvas.habibi-secure-canvas');
    canvases.forEach(canvas => {
      if (typeof canvas._habibiRender === 'function') {
        try {
          canvas._habibiRender();
        } catch (e) {}
      }
    });
  }

  function poisonClipboard() {
    const writeNotice = () => {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(COPYRIGHT_NOTICE).catch(() => {});
        }
      } catch (err) {}
    };

    writeNotice();
    // Multi-sweep execution to override asynchronous OS screenshot clipboard updates (e.g. Snipping Tool)
    const sweepDelays = [20, 50, 100, 200, 350, 600, 1000, 1500, 2500];
    sweepDelays.forEach(delay => {
      setTimeout(writeNotice, delay);
    });
  }

  function activateGuard() {
    initSecurityElements();
    blankAllCanvases();
    const guard = document.getElementById('habibi-screenshot-guard');
    const main = document.querySelector('main') || document.body;
    if (guard) guard.classList.add('active');
    if (main) main.classList.add('habibi-privacy-blur');
    document.body.classList.add('habibi-screenshot-active');
  }

  function deactivateGuard() {
    const guard = document.getElementById('habibi-screenshot-guard');
    const main = document.querySelector('main') || document.body;
    if (guard) guard.classList.remove('active');
    if (main) main.classList.remove('habibi-privacy-blur');
    document.body.classList.remove('habibi-window-blurred');
    document.body.classList.remove('habibi-screenshot-active');
    restoreAllCanvases();
  }

  function triggerAntiScreenshot() {
    activateGuard();
    poisonClipboard();
    showToast('Screenshots of proprietary artwork are strictly prohibited.');
  }

  // =========================================================================
  // 4. INTERACTION LOCKS (Right Click, Drag, Shortcuts)
  // =========================================================================
  document.addEventListener('contextmenu', function (e) {
    e.preventDefault();
    e.stopPropagation();
    showToast('Right-click is disabled to protect proprietary artwork.');
    return false;
  }, { capture: true });

  document.addEventListener('dragstart', function (e) {
    e.preventDefault();
    e.stopPropagation();
    showToast('Asset downloading is restricted.');
    return false;
  }, { capture: true });

  document.addEventListener('keydown', function (e) {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

    if (e.key === 'F12' || (ctrlKey && e.shiftKey && ['I', 'i', 'J', 'j', 'C', 'c'].includes(e.key))) {
      e.preventDefault();
      e.stopPropagation();
      activateGuard();
      showToast('Developer tools access is restricted.');
      return false;
    }

    if (ctrlKey && (e.key === 'U' || e.key === 'u')) {
      e.preventDefault();
      e.stopPropagation();
      activateGuard();
      showToast('Source view is disabled.');
      return false;
    }

    if (ctrlKey && (e.key === 'S' || e.key === 's')) {
      e.preventDefault();
      e.stopPropagation();
      activateGuard();
      showToast('Page saving is disabled.');
      return false;
    }

    if (ctrlKey && (e.key === 'P' || e.key === 'p')) {
      e.preventDefault();
      e.stopPropagation();
      activateGuard();
      showToast('Printing artwork is restricted.');
      return false;
    }

    // Detect screenshot hotkeys across Windows, Mac, Mobile & Browser tools
    const isPrintScreen = e.key === 'PrintScreen' || e.key === 'Snapshot' || e.code === 'PrintScreen' || e.keyCode === 44;
    const isSnippingTool = (e.metaKey || e.ctrlKey) && e.shiftKey && ['S', 's'].includes(e.key);
    const isMacScreenshot = isMac && e.metaKey && e.shiftKey && ['3', '4', '5', '6'].includes(e.key);
    const isAltPrintScreen = e.altKey && isPrintScreen;

    // Physical mobile buttons used in screenshot combinations (Power + VolumeDown / Camera Shutter)
    const isMobileScreenshotKey = 
      e.key === 'VolumeDown' ||
      e.key === 'VolumeUp' ||
      e.code === 'AudioVolumeDown' ||
      e.code === 'AudioVolumeUp' ||
      e.keyCode === 24 || // Volume Up
      e.keyCode === 25 || // Volume Down
      e.key === 'Camera' ||
      e.keyCode === 27;   // Camera button

    if (isPrintScreen || isSnippingTool || isMacScreenshot || isAltPrintScreen || isMobileScreenshotKey) {
      e.preventDefault();
      e.stopPropagation();
      triggerAntiScreenshot();
      return false;
    }
  }, { capture: true });

  // Catch PrintScreen & Snipping Tool on keyup
  document.addEventListener('keyup', function (e) {
    const isPrintScreen = e.key === 'PrintScreen' || e.key === 'Snapshot' || e.code === 'PrintScreen' || e.keyCode === 44;
    const isSnippingTool = (e.metaKey || e.ctrlKey) && e.shiftKey && ['S', 's'].includes(e.key);
    const isMobileScreenshotKey = e.key === 'VolumeDown' || e.key === 'VolumeUp' || e.keyCode === 24 || e.keyCode === 25;
    if (isPrintScreen || isSnippingTool || isMobileScreenshotKey) {
      triggerAntiScreenshot();
    }
  }, { capture: true });

  window.addEventListener('keyup', function (e) {
    const isPrintScreen = e.key === 'PrintScreen' || e.key === 'Snapshot' || e.code === 'PrintScreen' || e.keyCode === 44;
    if (isPrintScreen) {
      triggerAntiScreenshot();
    }
  }, { capture: true });

  // =========================================================================
  // 4B. MOBILE ANTI-SCREENSHOT GESTURE & MULTI-TOUCH ENGINE
  // =========================================================================
  // Blocks Android 3-finger swipe down (Xiaomi, OnePlus, Oppo, Vivo, Motorola, Realme) & Samsung palm swipe
  window.addEventListener('touchstart', function (e) {
    if (e.touches && e.touches.length >= 2) {
      // 2 or more touches detected - standard mobile screenshot gesture attempt
      e.preventDefault();
      e.stopPropagation();
      triggerAntiScreenshot();
      return false;
    }

    // Palm swipe contact area detection (Samsung Galaxy)
    if (e.touches) {
      for (let i = 0; i < e.touches.length; i++) {
        const t = e.touches[i];
        if ((t.radiusX && t.radiusX > 28) || (t.radiusY && t.radiusY > 28)) {
          e.preventDefault();
          triggerAntiScreenshot();
          return false;
        }
      }
    }
  }, { passive: false, capture: true });

  window.addEventListener('touchmove', function (e) {
    if (e.touches && e.touches.length >= 2) {
      e.preventDefault();
      e.stopPropagation();
      triggerAntiScreenshot();
      return false;
    }
  }, { passive: false, capture: true });

  // Prevent mobile long-press image download / Google Lens menus
  let mobileTouchHoldTimer = null;
  window.addEventListener('touchstart', function (e) {
    const target = e.target;
    if (target && (target.closest('.habibi-secure-canvas') || target.closest('.media') || target.closest('picture') || target.closest('.card__media') || target.closest('.product__media') || target.closest('img'))) {
      mobileTouchHoldTimer = setTimeout(() => {
        showToast('Direct image saving and screenshot extraction are restricted.');
      }, 350);
    }
  }, { passive: true, capture: true });

  window.addEventListener('touchend', function () {
    if (mobileTouchHoldTimer) clearTimeout(mobileTouchHoldTimer);
  }, { passive: true });

  window.addEventListener('touchcancel', function () {
    if (mobileTouchHoldTimer) clearTimeout(mobileTouchHoldTimer);
  }, { passive: true });

  // Mobile screen orientation change protection
  window.addEventListener('orientationchange', function () {
    activateGuard();
    setTimeout(() => {
      deactivateGuard();
    }, 400);
  });

  // INSTANT ANTI-SNIPPING TOOL & PRIVACY SHIELD
  // When user activates Snipping Tool (Win+Shift+S), mobile pulls down control center, or window loses focus, immediately blackout page
  function onWindowLostFocus() {
    document.body.classList.add('habibi-window-blurred');
    activateGuard();
    poisonClipboard();
  }

  function onWindowRegainedFocus() {
    // Overwrite any screen grab that Snipping Tool or mobile screenshot UI deposited into the clipboard
    poisonClipboard();
    // Guard remains active until explicitly dismissed by clicking "Resume Gallery Viewing" or pressing Escape
  }

  window.addEventListener('blur', onWindowLostFocus);
  window.addEventListener('focus', onWindowRegainedFocus);

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      onWindowLostFocus();
    } else {
      onWindowRegainedFocus();
    }
  });

  window.addEventListener('pagehide', onWindowLostFocus);
  document.addEventListener('freeze', onWindowLostFocus);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      deactivateGuard();
    }
  });

  // =========================================================================
  // 5. UNIVERSAL CANVAS CONVERTER & DOM URL PURGER (True Extension Killer)
  // =========================================================================
  function convertImageToSecureCanvas(img) {
    if (!img || img.dataset.habibiConverted === 'true' || img.tagName !== 'IMG') return;

    const rawSrc = img.currentSrc || img.src || img.getAttribute('src');
    if (!rawSrc || rawSrc.startsWith('data:image/svg+xml') || rawSrc === BLANK_DUMMY_DATA_URL) return;

    img.dataset.habibiConverted = 'true';
    const parent = img.parentElement;
    if (!parent) return;

    // Remove any sibling <source> tags if inside a <picture> element
    if (parent.tagName === 'PICTURE') {
      const sources = parent.querySelectorAll('source');
      sources.forEach(s => s.remove());
    }

    // Create GPU canvas
    const canvas = document.createElement('canvas');
    canvas.className = (img.className || '') + ' habibi-secure-canvas';
    canvas.dataset.habibiProtected = 'true';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.setAttribute('role', 'img');
    if (img.alt) canvas.setAttribute('aria-label', img.alt);

    // Match original image display styling
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.objectFit = window.getComputedStyle(img).objectFit || 'cover';
    canvas.style.objectPosition = window.getComputedStyle(img).objectPosition || 'center center';
    canvas.style.display = 'block';
    canvas.style.pointerEvents = 'auto';

    const offscreenImg = new Image();
    offscreenImg.crossOrigin = 'anonymous';

    function renderToCanvas() {
      const containerRect = (parent && parent.getBoundingClientRect().width > 0)
        ? parent.getBoundingClientRect()
        : null;
      const rect = containerRect || img.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      
      const width = Math.max(rect.width || offscreenImg.naturalWidth || 300, 10);
      const height = Math.max(rect.height || offscreenImg.naturalHeight || 300, 10);

      const computedFit = (window.getComputedStyle(img).objectFit || '').toLowerCase();
      const isContain = computedFit === 'contain' ||
        img.classList.contains('ah-curator-table__featured-img') ||
        img.classList.contains('ah-curator-card__img') ||
        img.classList.contains('ah-dossier__primary-img') ||
        Boolean(img.closest('.ah-curator-table__featured-media')) ||
        Boolean(img.closest('.ah-curator-card__media')) ||
        Boolean(img.closest('.ah-dossier__canvas-stage'));

      const imgW = offscreenImg.naturalWidth || width;
      const imgH = offscreenImg.naturalHeight || height;
      const imgRatio = imgW / imgH;

      if (isContain && imgRatio > 0) {
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(canvas.width / imgRatio);
      } else {
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      const targetRatio = canvas.width / canvas.height;

      if (isContain) {
        canvas.style.objectFit = 'contain';
        canvas.style.objectPosition = 'center center';
        canvas.style.margin = '0 auto';
        canvas.classList.add('is-contain');
        ctx.drawImage(offscreenImg, 0, 0, imgW, imgH, 0, 0, canvas.width, canvas.height);
      } else {
        let sx = 0, sy = 0, sw = imgW, sh = imgH;
        if (imgRatio > targetRatio) {
          sw = imgH * targetRatio;
          sx = (imgW - sw) / 2;
        } else {
          sh = imgW / targetRatio;
          sy = (imgH - sh) / 2;
        }
        ctx.drawImage(offscreenImg, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      }

      // Embedded microscopic security watermark in pixel stream
      ctx.save();
      ctx.fillStyle = 'rgba(197, 160, 89, 0.04)';
      ctx.font = `bold ${Math.max(11 * dpr, 13)}px sans-serif`;
      ctx.rotate(-Math.PI / 6);
      for (let x = -canvas.width; x < canvas.width * 2; x += 180 * dpr) {
        for (let y = -canvas.height; y < canvas.height * 2; y += 95 * dpr) {
          ctx.fillText('HABIBI ARTS & CRAFTS', x, y);
        }
      }
      ctx.restore();

      // Permanent pixel-level archival stamp (protects against phone screen photographs & mobile grabs)
      ctx.save();
      ctx.fillStyle = 'rgba(197, 160, 89, 0.12)';
      ctx.font = `600 ${Math.max(10 * dpr, 12)}px 'Plus Jakarta Sans', sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText('© HABIBI ARTS & CRAFTS • ARCHIVAL', canvas.width - (12 * dpr), canvas.height - (10 * dpr));
      ctx.restore();

      // COMPLETE DOM PURGE: Wipe real image URLs from DOM so extensions scanning DOM find 0 URLs
      img.removeAttribute('srcset');
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
      img.src = BLANK_DUMMY_DATA_URL;
      img.style.display = 'none';
      img.style.visibility = 'hidden';
    }

    // Attach security repaint handler
    canvas._habibiRender = renderToCanvas;

    offscreenImg.onload = function () {
      if (!parent.contains(canvas)) {
        parent.insertBefore(canvas, img);
      }
      if (parent && !parent.querySelector('.habibi-artwork-shield')) {
        const shield = document.createElement('div');
        shield.className = 'habibi-artwork-shield';
        parent.style.position = 'relative';
        parent.appendChild(shield);
      }
      renderToCanvas();

      window.addEventListener('resize', () => {
        if (document.body.contains(canvas) && !document.body.classList.contains('habibi-screenshot-active') && !document.body.classList.contains('habibi-window-blurred')) {
          renderToCanvas();
        }
      }, { passive: true });
    };

    offscreenImg.onerror = function () {
      // Robust fallback if local dev or CDN CORS blocks canvas drawing
      img.removeAttribute('srcset');
      img.setAttribute('draggable', 'false');
      img.setAttribute('oncontextmenu', 'return false;');
      img.style.userSelect = 'none';
      img.style.webkitTouchCallout = 'none';
      if (parent && !parent.querySelector('.habibi-artwork-shield')) {
        const shield = document.createElement('div');
        shield.className = 'habibi-artwork-shield';
        parent.style.position = 'relative';
        parent.appendChild(shield);
      }
    };

    offscreenImg.src = rawSrc;
  }

  // =========================================================================
  // 6. PROCESS ALL IMAGES, LOGOS, SHOWCASES, ICONS
  // =========================================================================
  function secureAllImages() {
    const allImages = document.querySelectorAll('img:not([src^="data:image/svg+xml"])');
    allImages.forEach(img => {
      convertImageToSecureCanvas(img);
    });

    // Also sanitize all remaining picture/source elements
    const allSources = document.querySelectorAll('picture source');
    allSources.forEach(s => {
      s.removeAttribute('srcset');
      s.remove();
    });

    // Clear any links pointing to images
    const imageLinks = document.querySelectorAll('a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".webp"], a[href$=".svg"]');
    imageLinks.forEach(a => {
      a.setAttribute('data-original-href', a.getAttribute('href'));
      a.setAttribute('href', '#');
    });
  }

  // =========================================================================
  // 7. REAL-TIME SYNCHRONOUS MUTATION OBSERVER
  // =========================================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initSecurityElements();
      secureAllImages();
    });
  } else {
    initSecurityElements();
    secureAllImages();
  }

  // Run initial scan immediately
  secureAllImages();

  // Watch for any dynamic elements inserted by Shopify or user interactions
  const observer = new MutationObserver(function (mutations) {
    for (let i = 0; i < mutations.length; i++) {
      const addedNodes = mutations[i].addedNodes;
      for (let j = 0; j < addedNodes.length; j++) {
        const node = addedNodes[j];
        if (node.nodeType === 1) {
          if (node.tagName === 'IMG') {
            convertImageToSecureCanvas(node);
          } else if (node.querySelectorAll) {
            const nestedImgs = node.querySelectorAll('img');
            nestedImgs.forEach(convertImageToSecureCanvas);
          }
        }
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  // Console copyright seal
  try {
    console.log(
      '%c✦ HABIBI ARTS & CRAFTS\n%cCurated Fine Art by Qaiser Habibi.\nAll digital assets, artwork designs, and photography are proprietary and protected under copyright laws.\nUnauthorized extraction, reproduction, or commercial use is strictly prohibited.',
      'font-size: 16px; font-weight: bold; color: #D4AF37; font-family: serif;',
      'font-size: 12px; color: #E5DCD0; line-height: 1.5;'
    );
  } catch (e) {}

})();
